import { ofType } from 'redux-observable';
import { of, EMPTY } from 'rxjs';
import { switchMap, catchError, mergeMap } from 'rxjs/operators';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { NotificationHubService } from '../../notification-hub/notification-hub-service';
import { NotificationType } from '../../notification-hub/notification-hub.types';
import Global from '../../../store/global-selectors';
import * as SellerActions from '../../../store/seller/seller.actions';
import { GenericErrorRetryContainer } from '../../../containers/generic-error-retry-container';
import * as Actions from './orders.import.actions';
import * as Api from './orders.import.api';
import { MapOrderImportModal } from '../../../components/modals/map-order-import-modal';
import { OrderImportMappingsRenameModalContainer } from '../../../containers/order-import-mappings-rename-modal-container';
import { OrderImportMappingsDeleteModalContainer } from '../../../containers/order-import-mappings-delete-modal-container';
import { MappingsImportLogModal } from '../../../components/modals/mappings-import-log-modal';
import { messages } from '../messages';
import { isCurrentUser, trackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import { FeatureFlags } from '../../../feature-flags/feature-flags';
import { FeatureFlagDeclarations } from '../../../feature-flags/feature-flag-declarations';
import { syncConsolidatedInventoryCounts } from '../../../pages/settings/inventory-sources/api';

const IMPORT_IN_PROGRESS_STATUS_CODE = 2;
const MAPPING = 'MAPPING';

export const getMappings = (action$) => {
  return action$.pipe(
    ofType(Actions.INITIATE_GET_IMPORT_MAPPINGS),
    switchMap(() => {
      return Api.getSavedMappings().pipe(
        mergeMap((response) => {
          return of(Actions.getMappingSuccess(response));
        }),
        catchError((error) => {
          return of(Actions.getMappingFailure(error));
        }),
      );
    }),
  );
};

export const openOrderImportMappingsModal = (action$) => {
  return action$.pipe(
    ofType(Actions.OPEN_ORDER_IMPORT_MAPPING_MODAL),
    mergeMap(() => {
      const modalId = ModalService.launchModal({
        component: MapOrderImportModal,
        onCancel: () => {
          trackWorkflow(Workflows.ORDER_IMPORT_FLOW, WorkflowSteps.CANCEL, {
            modalStep: MAPPING,
          });
        },
      });
      return of(Actions.orderImportMappingModalOpened(modalId));
    }),
  );
};

export const openMappingsDeleteModal = (action$) => {
  return action$.pipe(
    ofType(Actions.OPEN_MAPPINGS_DELETE_MODAL),
    mergeMap(({ mappingName }) => {
      ModalService.launchModal({
        component: OrderImportMappingsDeleteModalContainer,
        props: { mappingName },
      });
      return EMPTY;
    }),
  );
};

export const deleteSavedMapping = (action$) => {
  return action$.pipe(
    ofType(Actions.DELETE_SAVED_IMPORT_MAPPING),
    switchMap(({ mappingName }) => {
      return Api.deleteSavedMapping(mappingName).pipe(
        mergeMap(() => {
          return of(Actions.getMappings());
        }),
      );
    }),
  );
};

export const upload = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.INITIATE_ORDERS_CSV_UPLOAD),
    switchMap((action) => {
      return Api.postFileUpload(action.mappingName, action.hasColumnHeaders, action.file).pipe(
        mergeMap((response) => {
          return of(Actions.uploadSuccess(response.data));
        }),
        catchError((error) => {
          const { Errors } = error.response.data;
          const ERROR_CODE_FOR_BLOCKED_CSV_IMPORT = '9a50d9ad-f2cb-46c5-82c0-c26666686e23';
          const hasCsvBlockedError = Errors.length && Errors[0]?.Message?.includes(ERROR_CODE_FOR_BLOCKED_CSV_IMPORT);

          if (hasCsvBlockedError) {
            const state = getState();
            const accountManager = Global.selectSellerAccountManager(state);
            const { name, email, phone } = accountManager;
            const hasAccountManager = name && (email || phone);
            const displayedMessage = hasAccountManager
              ? messages.csvImportBlockedWithAccountManagerMessage
              : messages.csvImportBlockedErrorMessage;
            const paramValues = {
              name,
              phone,
              email,
              br: <br />,
            };
            ModalService.launchModal({
              component: GenericErrorRetryContainer,
              props: {
                action,
                title: <FormattedMessage {...messages.csvImportBlockedErrorTitle} />,
                message: <FormattedMessage {...displayedMessage} values={paramValues} />,
              },
            });
          }

          return of(Actions.uploadFailure(error));
        }),
      );
    }),
  );
};

export const afterUploadSuccess = (action$) => {
  return action$.pipe(
    ofType(Actions.UPLOAD_ORDERS_CSV_SUCCESS),
    mergeMap(() => {
      return of(Actions.openOrderImportMappingsModal());
    }),
  );
};

export const openMappingsManagerModal = (action$) => {
  return action$.pipe(
    ofType(Actions.OPEN_MAPPINGS_MANAGER_MODAL),
    mergeMap(() => {
      ModalService.launchModal({
        component: OrderImportMappingsRenameModalContainer,
      });
      return EMPTY;
    }),
  );
};

export const startFileImport = (action$) => {
  return action$.pipe(
    ofType(Actions.INITIATE_START_IMPORT_ORDERS),
    switchMap((action) => {
      return Api.startFileImport(action.mappingsPayload).pipe(
        mergeMap((response) => {
          return of(Actions.fileImportSuccess(response.config.data));
        }),
        catchError((error) => {
          return of(Actions.fileImportFailure(error));
        }),
      );
    }),
  );
};

export const openImportProgressNotification = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.INITIATE_START_IMPORT_ORDERS),
    mergeMap(() => {
      const state = getState();
      const mappingModalId = Global.selectOrdersImportMappingModalId(state);
      ModalService.closeModalById(mappingModalId);
      const storeRefreshSuccessNotificationProps = {
        title: <FormattedMessage {...messages.importNotificationTitle} />,
        content: <FormattedMessage {...messages.importNotificationBody} />,
        notificationType: 'info',
        progress: true,
        dontHide: true,
      };
      const notificationId = NotificationService.showNotification({
        ...storeRefreshSuccessNotificationProps,
      });

      return of(Actions.orderImportProgressNotificationOpened(notificationId));
    }),
  );
};

export const renameSavedMapping = (action$) => {
  return action$.pipe(
    ofType(Actions.RENAME_SAVED_IMPORT_MAPPING),
    switchMap((action) => {
      const { mappingName, updatedName } = action;
      return Api.renameSavedMapping(mappingName, updatedName).pipe(
        mergeMap(() => {
          return of(Actions.getMappings());
        }),
        catchError((error) => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.getMappingsErrorTitle} />,
              message: <FormattedMessage {...messages.getMappingsErrorMessage} />,
            },
          });
          return of(Actions.renameSavedMappingError(error));
        }),
      );
    }),
  );
};

export const afterStoreUpdate = (action$, { getState }) => {
  return action$.pipe(
    ofType(SellerActions.UPDATE_STORE_AFTER_REFRESH),
    switchMap((action) => {
      const {
        data: { Status, StoreGUID, NewOrders, LastUpdateTime, uniqueMessageId, UserName },
      } = action;
      const state = getState();
      const importGuid = Global.selectOrdersImportBatchNumber(state);
      const stores = Global.selectSellerActiveManualStores(state);
      const selectedStore = Global.selectOrdersImportSelectedStore(state, stores);
      const shouldTrackEvent = isCurrentUser(state, UserName);
      if (!selectedStore || selectedStore.storeGuid !== StoreGUID || Status === IMPORT_IN_PROGRESS_STATUS_CODE) {
        return EMPTY;
      }

      const notificationId = Global.selectOrdersImportImportProgressNotificationId(state);
      NotificationService.removeNotification(notificationId);

      const isNewOrderImportEnabled = new FeatureFlags({ state }).enableNewOrderImport;
      if (isNewOrderImportEnabled) {
        return EMPTY;
      }

      if (shouldTrackEvent) {
        trackWorkflow(Workflows.ORDER_IMPORT_FLOW, WorkflowSteps.COMPLETE, {
          Status,
          StoreGUID,
          NewOrders,
          LastUpdateTime,
          uniqueMessageId,
        });
      }
      // Inventory Sync Bandaid
      const sellerFeatures = Global.selectSellerSellerFeatures(state);
      const hasInventorySync = sellerFeatures.includes('InventorySync');

      const { featureFlags } = state;
      const featureFlagService = new FeatureFlags({ featureFlags });
      const shouldSyncInventoryOnOrderImport = featureFlagService.getFeatureFlag(FeatureFlagDeclarations.enableSyncInventoryOnOrderImport);

      if (hasInventorySync && shouldSyncInventoryOnOrderImport) {
        syncConsolidatedInventoryCounts();
      }

      return Api.fetchImportLog(importGuid) // TODO jvera: sync inventory
        .pipe(
          mergeMap(({ data }) => {
            const messageArray = data.htmlMessage.split('<br>');
            const errors = messageArray.filter((item) => {
              return item.match(/fail|error|(already exists)/);
            });
            const failedCount = errors.length;

            NotificationHubService.showNotification(
              {
                notificationType: NotificationType.ORDERS_IMPORTED,
                defaultTitle: messages.importSuccessNotificationTitle.defaultMessage,
                defaultDescription: `Your order import finished successfully.${failedCount > 0 ? ` ${failedCount} orders failed to import.` : ''}`,
                metadataJson: JSON.stringify({ failedCount }),
              },
              {
                title: <FormattedMessage {...messages.importSuccessNotificationTitle} />,
                content: <FormattedMessage {...messages.importSuccessNotificationTitle} />,
                notificationType: NOTIFICATION_TYPE_SUCCESS,
                dontHide: true,
              },
            );

            if (shouldTrackEvent && failedCount) {
              trackWorkflow(Workflows.ORDER_IMPORT_FLOW, WorkflowSteps.ERROR, {
                Status,
                StoreGUID,
                NewOrders,
                LastUpdateTime,
                uniqueMessageId,
                error: errors.join('<br>'),
                errorCount: failedCount,
              });
            }
            if (!data.htmlMessage.match(/fail|error|(already exists)/)) {
              return EMPTY;
            }
            return of(Actions.orderImportStatusLogSuccess(data));
          }),
          catchError((error) => {
            NotificationHubService.showNotification(
              {
                notificationType: NotificationType.ORDERS_IMPORTED,
                defaultTitle: messages.importSuccessNotificationTitle.defaultMessage,
                defaultDescription: 'Your order import finished successfully.',
              },
              {
                title: <FormattedMessage {...messages.importSuccessNotificationTitle} />,
                content: <FormattedMessage {...messages.importSuccessNotificationTitle} />,
                notificationType: NOTIFICATION_TYPE_SUCCESS,
                dontHide: true,
              },
            );
            if (shouldTrackEvent) {
              trackWorkflow(Workflows.ORDER_IMPORT_FLOW, WorkflowSteps.ERROR, {
                Status,
                StoreGUID,
                NewOrders,
                LastUpdateTime,
                uniqueMessageId,
                error,
              });
            }
            return of(Actions.orderImportStatusLogFailure(error));
          }),
        );
    }),
  );
};

export const afterImportLogSuccess = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_ORDER_IMPORT_STATUS_LOG_SUCCESS),
    mergeMap(() => {
      ModalService.launchModal({
        component: MappingsImportLogModal,
      });
      return EMPTY;
    }),
  );
};
